import BaseApi from "./base-api";
import {
  CompletePurchaseReturnPayload,
  CreatePurchaseReturnPayload,
  PurchaseReturn,
  PurchaseReturnFilters,
  PurchaseReturnPagination,
  PurchaseReturnStats,
  ReturnableItemsResponse,
  UpdatePurchaseReturnPayload,
  UpdateRefundStatusPayload,
} from "../types/PurchaseReturn";

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  message?: string;
  errors?: unknown;
};

export const PurchaseReturnsApi = {
  list: async (
    page = 1,
    limit = 10,
    filters?: PurchaseReturnFilters,
  ): Promise<{
    success: boolean;
    data: PurchaseReturn[];
    pagination?: PurchaseReturnPagination;
    message?: string;
  }> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.status && filters.status !== "all") {
      params.append("status", filters.status);
    }
    if (filters?.supplierId && filters.supplierId !== "all") {
      params.append("supplierId", filters.supplierId);
    }
    if (filters?.purchaseOrderId) {
      params.append("purchaseOrderId", filters.purchaseOrderId);
    }
    if (filters?.search?.trim()) {
      params.append("search", filters.search.trim());
    }
    if (filters?.startDate) {
      params.append("startDate", filters.startDate);
    }
    if (filters?.endDate) {
      params.append("endDate", filters.endDate);
    }

    return BaseApi.get(`/purchase-returns?${params.toString()}`);
  },

  getStats: (): Promise<ApiResponse<PurchaseReturnStats>> =>
    BaseApi.get("/purchase-returns/stats/overview"),

  getReturnableItems: (
    purchaseOrderId: string,
  ): Promise<ApiResponse<ReturnableItemsResponse>> =>
    BaseApi.get(
      `/purchase-returns/purchase-orders/${purchaseOrderId}/returnable-items`,
    ),

  getById: (id: string): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.get(`/purchase-returns/${id}`),

  create: (
    payload: CreatePurchaseReturnPayload,
  ): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.post("/purchase-returns", payload),

  update: (
    id: string,
    payload: UpdatePurchaseReturnPayload,
  ): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.put(`/purchase-returns/${id}`, payload),

  approve: (id: string, notes?: string): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.patch(`/purchase-returns/${id}/approve`, notes ? { notes } : {}),

  reject: (id: string, reason: string): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.patch(`/purchase-returns/${id}/reject`, { reason }),

  complete: (
    id: string,
    payload: CompletePurchaseReturnPayload,
  ): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.post(`/purchase-returns/${id}/complete`, payload),

  cancel: (id: string, reason?: string): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.post(`/purchase-returns/${id}/cancel`, reason ? { reason } : {}),

  updateRefundStatus: (
    id: string,
    payload: UpdateRefundStatusPayload,
  ): Promise<ApiResponse<PurchaseReturn>> =>
    BaseApi.patch(`/purchase-returns/${id}/refund-status`, payload),

  remove: (id: string): Promise<ApiResponse<unknown>> =>
    BaseApi.delete(`/purchase-returns/${id}`),

  // Same approach as the purchase order PDF download: BaseApi always parses
  // JSON, so the blob is fetched directly with the bearer token.
  downloadPdf: async (id: string) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "");
    if (!baseUrl) {
      throw new Error("API base URL is not configured");
    }

    const token = localStorage.getItem("authToken");
    const response = await fetch(`${baseUrl}/purchase-returns/${id}/pdf`, {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

    if (!response.ok) {
      let message = `Download failed (${response.status})`;
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
      } catch {
        // non-JSON error body, keep the generic message
      }
      throw new Error(message);
    }

    // Guard against a JSON error body served with a 2xx status
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      let message = "Download failed";
      try {
        const body = await response.json();
        if (body?.message) message = body.message;
      } catch {
        // keep the generic message
      }
      throw new Error(message);
    }

    return {
      blob: await response.blob(),
      contentDisposition: response.headers.get("content-disposition"),
    };
  },
};
